import { CyraError } from "./errors";
import {
  bodyBoundUpdateBinding,
  createMutationPath,
  createProposalBinding,
  directCreateBinding,
  directUpdateBinding,
  existingMutationDefinitions,
  type UpdateOutcomeBinding,
  updateMutationPath,
  updateProposalBinding,
} from "./mutation-outcome-registry";

const storageKey = "cyra.pending-mutation-intents.v2";
const legacyStorageKey = "cyra.pending-mutation-intents.v1";
const maxPending = 1_000;

export type MutationIntent = {
  fingerprint: string;
  key: string;
  operationFingerprint?: string;
  sourceFingerprint?: string;
};
// A fresh, exclusive send can accept an explicit rollback proof. Reloads, retries and
// transport failures cannot recreate this process-local proof of no earlier attempt.
const freshAttempts = new Map<string, MutationIntent>();

// Only opaque SHA-256 fingerprints and random keys are retained; never request bodies,
// contacts, tokens or responses. Uncertain outcomes must survive a route change/reload.
export class MutationIntents {
  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem">) {}

  reserve(
    fingerprint: string,
    proposedKey: string,
    operationFingerprint?: string,
    sourceFingerprint?: string,
    conflictingScopes: readonly string[] = [],
  ): MutationIntent {
    const pending = this.read();
    const existing = pending.find((entry) => entry.fingerprint === fingerprint);
    if (existing) {
      if (existing.operationFingerprint && existing.operationFingerprint !== operationFingerprint)
        this.unavailable("Saved operation scope does not match this retry.");
      if (existing.sourceFingerprint && existing.sourceFingerprint !== sourceFingerprint)
        this.unavailable("Saved operation source does not match this retry.");
      freshAttempts.delete(existing.key);
      if (operationFingerprint && !existing.operationFingerprint) {
        existing.operationFingerprint = operationFingerprint;
        if (sourceFingerprint) existing.sourceFingerprint = sourceFingerprint;
        this.write(pending);
      }
      return existing;
    }
    if (operationFingerprint) {
      if (pending.some((entry) => !entry.operationFingerprint))
        this.unavailable(
          "Legacy unconfirmed saves require exact retry or verified reconciliation before a new save.",
        );
      if (
        pending.some(
          (entry) =>
            entry.operationFingerprint === operationFingerprint ||
            (entry.operationFingerprint !== undefined &&
              conflictingScopes.includes(entry.operationFingerprint)),
        )
      )
        throw new CyraError({
          code: "MUTATION_OUTCOME_UNRESOLVED",
          message:
            "An earlier save for this operation is unresolved. Reconcile its exact outcome before submitting an edited draft.",
          requestId: "client",
        });
    }
    if (pending.length >= maxPending)
      this.unavailable(
        "Too many unconfirmed saves. Reconcile pending operations before submitting more.",
      );
    const intent = {
      fingerprint,
      key: proposedKey,
      ...(operationFingerprint ? { operationFingerprint } : {}),
      ...(sourceFingerprint ? { sourceFingerprint } : {}),
    };
    this.write([...pending, intent]);
    freshAttempts.set(intent.key, intent);
    return intent;
  }

  pending(operationFingerprint: string): MutationIntent | undefined {
    return this.read().find((entry) => entry.operationFingerprint === operationFingerprint);
  }

  uncertain(intent: MutationIntent): void {
    freshAttempts.delete(intent.key);
  }

  notApplied(intent: MutationIntent): boolean {
    if (freshAttempts.get(intent.key) !== intent) return false;
    this.complete(intent);
    return true;
  }

  complete(intent: MutationIntent): void {
    this.write(
      this.read().filter(
        (entry) => entry.fingerprint !== intent.fingerprint || entry.key !== intent.key,
      ),
    );
    freshAttempts.delete(intent.key);
  }

  private read(): MutationIntent[] {
    try {
      // v1 used a permission/epoch fingerprint. It cannot safely be reverse-mapped to a
      // stable actor, so never silently abandon those uncertain operation keys on upgrade.
      const legacy = this.storage.getItem(legacyStorageKey);
      if (legacy !== null && legacy !== "[]")
        this.unavailable("Legacy unconfirmed saves require reconciliation before saving again.");
      const values: unknown = JSON.parse(this.storage.getItem(storageKey) ?? "[]");
      if (
        !Array.isArray(values) ||
        values.length > maxPending ||
        values.some(
          (entry) =>
            !entry ||
            typeof entry !== "object" ||
            typeof entry.fingerprint !== "string" ||
            !/^[a-f0-9]{64}$/u.test(entry.fingerprint) ||
            typeof entry.key !== "string" ||
            entry.key.length < 16 ||
            entry.key.length > 128 ||
            (entry.operationFingerprint !== undefined &&
              (typeof entry.operationFingerprint !== "string" ||
                !/^[a-f0-9]{64}$/u.test(entry.operationFingerprint))) ||
            (entry.sourceFingerprint !== undefined &&
              (typeof entry.sourceFingerprint !== "string" ||
                !/^[a-f0-9]{64}$/u.test(entry.sourceFingerprint) ||
                !entry.operationFingerprint)) ||
            Object.keys(entry).some(
              (key) =>
                !["fingerprint", "key", "operationFingerprint", "sourceFingerprint"].includes(key),
            ),
        )
      ) {
        this.unavailable(
          "Saved operation receipts are invalid. Reconcile them before submitting another save.",
        );
      }
      if (new Set(values.map((entry) => entry.fingerprint)).size !== values.length)
        this.unavailable(
          "Saved operation receipts contain conflicting identities. Reconcile them before saving.",
        );
      const scopes = values.map((entry) => entry.operationFingerprint).filter(Boolean);
      if (new Set(scopes).size !== scopes.length)
        this.unavailable("Saved operation scopes contain conflicting unresolved saves.");
      return values as MutationIntent[];
    } catch (error) {
      if (error instanceof CyraError) throw error;
      return this.unavailable(
        "Saved operation receipts cannot be read. This save has not been sent.",
      );
    }
  }

  private write(values: MutationIntent[]): void {
    try {
      this.storage.setItem(storageKey, JSON.stringify(values));
    } catch {
      this.unavailable("Operation receipts cannot be retained. This save cannot safely continue.");
    }
  }

  private unavailable(message: string): never {
    throw new CyraError({ code: "MUTATION_RECEIPT_UNAVAILABLE", message, requestId: "client" });
  }
}

export async function mutationFingerprint(actorId: string, request: Request): Promise<string> {
  const text = await request.clone().text();
  const body =
    request.headers.get("content-type")?.includes("application/json") && text
      ? canonicalJson(JSON.parse(text))
      : text;
  return opaqueFingerprint(
    // Permission/epoch/scope and session changes must not turn a retry into a new intent.
    // The server reauthorizes the original key and can require explicit reconciliation.
    JSON.stringify([actorId, request.method, request.url, body]),
  );
}

/** Operation/target identity deliberately excludes draft body, epoch and session. */
export function mutationOperationFingerprint(
  actorId: string,
  request: Pick<Request, "method" | "url">,
  target = "",
): Promise<string> {
  return opaqueFingerprint(JSON.stringify([actorId, request.method, request.url, target]));
}

/** Compatibility predicate; all registered typed create aliases use the same closed classifier. */
export function isPartnerCreateProposal(value: unknown): boolean {
  return createProposalBinding(value)?.operation === "PARTNER_CREATE";
}

export async function mutationRequestScope(
  actorId: string,
  request: Request,
): Promise<{
  operationFingerprint: string;
  sourceFingerprint?: string;
  ownedCreate?: true;
  conflictingScopes?: readonly string[];
}> {
  const sourceFingerprint = await mutationOperationFingerprint(actorId, request);
  if (
    request.method === "POST" &&
    new URL(request.url).pathname.endsWith("/v1/admin/approval-requests") &&
    request.headers.get("content-type")?.includes("application/json")
  ) {
    const payload: unknown = await request.clone().json();
    const update = updateProposalBinding(payload);
    if (update) return updateRequestScope(actorId, request, update, sourceFingerprint);
    const binding = createProposalBinding(payload);
    if (!binding) return { operationFingerprint: sourceFingerprint };
    const url = new URL(request.url);
    url.pathname = createMutationPath(binding.operation, binding.targetId);
    url.search = "";
    return {
      operationFingerprint: await mutationOperationFingerprint(actorId, {
        method: "POST",
        url: url.href,
      }),
      sourceFingerprint,
      ownedCreate: true,
    };
  }
  const path = new URL(request.url).pathname;
  const bodyBound =
    request.headers.get("content-type")?.includes("application/json") &&
    /^\/v1\/admin\/partner-imports\/batches\/[^/]+\/retention\/purge-retry$/u.test(path)
      ? bodyBoundUpdateBinding(request, await request.clone().json())
      : undefined;
  const update = bodyBound ?? directUpdateBinding(request);
  if (update) return updateRequestScope(actorId, request, update, sourceFingerprint);
  const binding = directCreateBinding(request);
  if (binding) {
    const url = new URL(request.url);
    url.pathname = createMutationPath(binding.operation, binding.targetId);
    url.search = "";
    return {
      operationFingerprint: await mutationOperationFingerprint(actorId, {
        method: "POST",
        url: url.href,
      }),
      ownedCreate: true,
    };
  }
  return { operationFingerprint: sourceFingerprint };
}

async function updateRequestScope(
  actorId: string,
  request: Request,
  binding: UpdateOutcomeBinding,
  sourceFingerprint: string,
) {
  const url = new URL(request.url);
  url.pathname = updateMutationPath(binding.operation, binding.targetId);
  url.search = "";
  const method = existingMutationDefinitions[binding.operation].method;
  const operationFingerprint = await mutationOperationFingerprint(
    actorId,
    {
      method,
      url: url.href,
    },
    binding.operation === "PARTNER_IMPORT_PURGE_RETRY" ? binding.targetId : "",
  );
  const conflictingScopes: string[] = [];
  if (binding.operation === "SCHEDULE_STATUS") {
    // Retain recognition of pre-registry archive intents; never make an old unresolved key disappear.
    url.pathname = updateMutationPath(binding.operation, binding.targetId, "SCHEDULE_ARCHIVE");
    conflictingScopes.push(await mutationOperationFingerprint(actorId, { method, url: url.href }));
  }
  if (binding.operation === "USER_SUSPEND" || binding.operation === "USER_RESTORE") {
    url.pathname = updateMutationPath(
      binding.operation,
      binding.targetId,
      binding.operation === "USER_SUSPEND" ? "ADMINISTRATOR_SUSPEND" : "ADMINISTRATOR_RESTORE",
    );
    conflictingScopes.push(await mutationOperationFingerprint(actorId, { method, url: url.href }));
  }
  return {
    operationFingerprint,
    ownedCreate: true as const,
    conflictingScopes,
    ...(sourceFingerprint === operationFingerprint ? {} : { sourceFingerprint }),
  };
}

export function assertPartnerCreateResult(value: unknown): void {
  if (
    !value ||
    typeof value !== "object" ||
    !("id" in value) ||
    typeof value.id !== "string" ||
    value.id.length < 1 ||
    value.id.length > 64 ||
    !("version" in value) ||
    !Number.isSafeInteger(value.version) ||
    Number(value.version) < 1
  )
    throw new CyraError({
      code: "MUTATION_OUTCOME_UNRESOLVED",
      message:
        "The create response has no valid owned result reference. Reconcile its exact outcome before submitting another draft.",
      requestId: "client",
    });
}

async function opaqueFingerprint(value: string): Promise<string> {
  const encoded = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(hash), (value) => value.toString(16).padStart(2, "0")).join("");
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(",")}}`;
  return JSON.stringify(value) ?? "null";
}
